import React from 'react';
import { Navigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import HomeAppBar from '../../components/home/HomeAppBar';
import HomeNavBar from '../../components/home/HomeNavBar';
import HomeRouting from '../../routing/HomeRouting';
import HomeFooterBar from '../../components/home/HomeFooterBar';
import HomeSnackbar from '../../components/home/HomeSnackbar';
import { Box, CircularProgress } from '@mui/material';
import { useAppSelector } from '../../hooks/useAppSelector';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import * as userSlice from '../../data/user.slice';
import * as lookupSlice from '../../data/lookup.slice';
import {
  initializeFullStory,
  setFullStoryUser,
} from '../../services/fullStory';

const PREFIX = 'Home';

const classes = {
  container: `${PREFIX}-container`,
  content: `${PREFIX}-content`,
  scroller: `${PREFIX}-scroller`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    flex: '0 1 100%',
    minWidth: 0,
  },

  [`& .${classes.content}`]: {
    display: 'flex',
    flexGrow: 1,
    paddingBottom: 0,
    backgroundColor: theme.palette.background.paper,
    // backgroundImage: 'url(/bggray.png)',
    backgroundRepeatX: 'repeat',
    backgroundRepeatY: 'no-repeat',
    overflowX: 'auto',
    overflowY: 'scroll',
  },

  [`& .${classes.scroller}`]: {
    flexGrow: 1,
    minWidth: 0,
    paddingBottom: 20,
    padding: theme.spacing(3),
  },
}));

export default function Home() {
  const dispatch = useAppDispatch();
  const loadingLoggedInUser = useAppSelector(
    userSlice.selectLoadingLoggedInUserInfo,
  );
  const loadedLoggedInUser = useAppSelector(
    userSlice.selectLoadedLoggedInUserInfo,
  );
  const loggedInUserNoAccess = useAppSelector(
    userSlice.selectLoggedInUserNoAccess,
  );
  const userInfo = useAppSelector(userSlice.selectLoggedInUserInfo);

  // Only lookups that the first screen actually renders are fetched here.
  // Everything else is declared by the component that reads it, via
  // `useLookups`, and fetched on demand — see LOOKUP_FANOUT_PLAN.md.
  //
  // `timeZone` stays app-wide: 18 components read it (including the `/today`
  // trips table) and it is needed to render times correctly on first paint.
  React.useEffect(() => {
    if (loadedLoggedInUser) {
      dispatch(lookupSlice.fetchLookupItemsAsync('timeZone'));
    }
  }, [loadedLoggedInUser]);

  React.useEffect(() => {
    void initializeFullStory('f1');
  }, []);

  React.useEffect(() => {
    if (userInfo?.emailAddress) {
      setFullStoryUser('f1', userInfo.emailAddress);
    }
  }, [userInfo?.emailAddress]);

  if (loggedInUserNoAccess) {
    return <Navigate to="/noaccess" replace />;
  }

  if (!loadedLoggedInUser) {
    return (
      <Root className={classes.container}>
        <Box className={classes.content}>
          {loadingLoggedInUser && (
            <CircularProgress size={100} sx={{ margin: 'auto' }} />
          )}
        </Box>
      </Root>
    );
  }

  return (
    <Root className={classes.container}>
      <>
        <HomeAppBar />
        <HomeSnackbar />
        <Box className={classes.content}>
          <>
            <HomeNavBar />
            <Box className={classes.scroller}>
              <HomeRouting />
            </Box>
          </>
        </Box>
        <HomeFooterBar />
      </>
    </Root>
  );
}
